import {
  all,
  call,
  cancelled,
  put,
  race,
  take,
  select,
  spawn,
  takeLatest
} from '@redux-saga/core/effects'

import { SEARCH, NAV_BRAND } from '@/config'
import { PORTAL_TYPE_YF, PORTAL_TYPE_RJA } from '@/config/constants'
import { REPLACE_STATE_ACTION } from '@/location'
import api from '@/services/api'
import { captureExtendedException } from '@/utils/assert'

import { JOB } from '../../search/constants'

import { NEW_SEARCH_TRIGGER_MANUAL, newSearch, nextPage } from './actions'
import {
  getResultsList,
  getResultsNextParams,
  getResultsTotalSize
} from './selectors'

function * newSearchTrigger (
  {
    type: actionType,
    payload: searchParams,
    meta: {
      trigger = NEW_SEARCH_TRIGGER_MANUAL
    } = {}
  }
) {
  // we handle REPLACE_STATE_ACTION in order to cancel any outstanding requests
  // and in that case just need to skip it
  if (actionType === String(REPLACE_STATE_ACTION)) {
    return
  }
  const { type } = searchParams
  const searchApi = api.search[type]
  if (!searchApi) throw new Error(`Invalid search type ${type}`)
  const { limit = SEARCH.types[type].paging.limit } = searchParams
  const requestParams = {
    ...searchParams,
    offset: 0,
    limit
  }

  yield put(newSearch.request(searchParams, { trigger }))

  // do not proceed with request - for yourfirm portal, it's handled by the PBK
  if ([PORTAL_TYPE_YF, PORTAL_TYPE_RJA].includes(NAV_BRAND.portal_type) && type === JOB && trigger === NEW_SEARCH_TRIGGER_MANUAL) {
    return window.location.reload()
  }

  try {
    const {
      data: {
        data: results,
        orderOptions,
        queryParams,
        resultSize,
        resultParams,
        resultSizeHighQuality,
        resultSizeLowQuality,
        spellcheck
      }
    } = yield call(searchApi, requestParams)
    const nextParams = { ...requestParams, limit, offset: limit }
    yield put(newSearch.success(results, {
      nextParams,
      orderOptions,
      queryParams,
      resultSize,
      resultParams,
      resultSizeHighQuality,
      resultSizeLowQuality,
      searchParams,
      spellcheck
    }))
  } catch (e) {
    yield put(newSearch.failure(e, { searchParams }))
  } finally {
    const canceled = yield cancelled()
    yield put(newSearch.fulfill({ canceled, searchParams }))
  }
}

function * watchNextPageTrigger () {
  let canceled
  while (true) {
    yield take([nextPage.trigger])

    const requestParams = yield select(getResultsNextParams)
    if (!requestParams) {
      continue
    }

    yield put(nextPage.request(requestParams))
    canceled = false
    try {
      const searchApi = api.search[requestParams.type]
      const {
        response,
        cancel
      } = yield race({
        response: call(searchApi, requestParams),
        cancel: take([newSearch.trigger, REPLACE_STATE_ACTION])
      })
      if (!cancel) {
        const {
          data: {
            data: results,
            resultSize,
            resultSizeHighQuality,
            resultSizeLowQuality
          }
        } = response
        const { limit, offset } = requestParams
        const nextParams = { ...requestParams, limit, offset: offset + limit }
        yield put(nextPage.success(results, {
          nextParams,
          requestParams,
          resultSize,
          resultSizeHighQuality,
          resultSizeLowQuality
        }))
      } else {
        canceled = true
      }
    } catch (e) {
      yield put(nextPage.failure(e, { requestParams }))
    } finally {
      canceled = canceled || (yield cancelled())
      yield put(nextPage.fulfill({ canceled, requestParams }))
    }
  }
}

function * checkTooMuchResults () {
  const [resultSize, list] = yield all([
    getResultsTotalSize, getResultsList
  ])

  if (resultSize < list.length) {
    // current url will be automatically gathered by sentry
    yield call(captureExtendedException,
      new Error('Received more result items than resultsSize'),
      {
        severity: 'warning',
        extra: {
          resultSize,
          listLength: list.length
        }
      }
    )
  }
}

export default all([
  takeLatest([newSearch.trigger, REPLACE_STATE_ACTION], newSearchTrigger),
  spawn(watchNextPageTrigger),
  takeLatest([newSearch.success, nextPage.success], checkTooMuchResults)
])
